/* =====================================================================
 * TeamSelectorModal — 사이드바 팀 전환 모달
 *
 * Rendered when the user taps the "Team" toggle and belongs to more
 * than one team. Kept purely presentational: the caller owns the
 * `open`/`onClose` state and the team-view mutation so the same modal
 * can be triggered from other surfaces (e.g. a future `/teams` page)
 * without pulling Sidebar state along.
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';
import { Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { TeamInfo } from '@auw/api-client';
import { formatTokens } from '@auw/workspace-utils';

export interface TeamSelectorModalProps {
  open: boolean;
  teams: readonly TeamInfo[];
  activeTeamId: 'personal' | string;
  onSelect: (teamId: string) => void;
  onClose: () => void;
}

export const TeamSelectorModal: React.FC<TeamSelectorModalProps> = ({
  open,
  teams,
  activeTeamId,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation('common');
  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('sidebar.selectTeam')}</Title>
          <CloseBtn onClick={onClose}>
            <X size={18} />
          </CloseBtn>
        </Header>
        <List>
          {teams.map((team) => {
            const hasMemberQuota =
              typeof team.my_tokens_allocated === 'number' &&
              typeof team.my_tokens_used === 'number';
            const remainingTokens = hasMemberQuota
              ? Math.max(
                  0,
                  (team.my_tokens_allocated ?? 0) -
                    (team.my_tokens_used ?? 0),
                )
              : Math.max(0, team.tokens_total - team.tokens_used);

            return (
              <Item
                key={team.team_id}
                $active={activeTeamId === team.team_id}
                onClick={() => {
                  onSelect(team.team_id);
                  onClose();
                }}
              >
                <IconWrap>
                  <Users size={18} />
                </IconWrap>
                <Info>
                  <Name>{team.name}</Name>
                  <Meta>
                    {t('sidebar.membersAndTokens', {
                      members: team.member_count,
                      tokens: formatTokens(remainingTokens),
                    })}
                  </Meta>
                </Info>
              </Item>
            );
          })}
        </List>
      </Content>
    </Overlay>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: fadeIn 0.15s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Content = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  padding: 24px;
  width: 100%;
  max-width: 340px;
  animation: slideUp 0.2s ease;
  @keyframes slideUp {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Item = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.borderLight};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
        `
      : css`
          background: transparent;
        `}

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'};
  }
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const Info = styled.div`
  min-width: 0;
  flex: 1;
`;

const Name = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 1px;
`;
